import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stListRoomMembers,
  stListRooms,
  stReconcileRoomMembers,
  StReconcileRoomMembersResult,
  StRoom,
  StRoomMember,
} from '../../../services/socialTradingService';
import { StIdCell, StBooleanChip, StScrollTable, StStatusChip, stFormatDateTime, stText } from '../StListUi';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PAGE_SIZE = 20;

export default function SocialTradingRoomMembersList() {
  const t = useTranslate();
  const notify = useNotify();
  const [rooms, setRooms] = useState<StRoom[]>([]);
  const [roomId, setRoomId] = useState('');
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [items, setItems] = useState<StRoomMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [lastReconcile, setLastReconcile] = useState<StReconcileRoomMembersResult | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await stListRooms({ limit: 200, offset: 0 });
        setRooms(res?.items || []);
        if (!roomId && res?.items?.length) {
          setRoomId(res.items[0].id);
        }
      } catch (e: any) {
        notify(stErrorMessage(e), { type: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify]);

  const load = useCallback(async () => {
    if (!roomId || !UUID_RE.test(roomId)) {
      setItems([]);
      setTotal(0);
      return;
    }
    if (userId.trim() && !UUID_RE.test(userId.trim())) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const res = await stListRoomMembers(roomId, {
        status: status || undefined,
        userId: userId.trim() || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [roomId, userId, status, page, notify, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onReconcile = async () => {
    if (!roomId || !UUID_RE.test(roomId)) {
      notify(t('module.socialTrading.roomMembersNeedRoom'), { type: 'warning' });
      return;
    }
    setReconciling(true);
    try {
      const result = await stReconcileRoomMembers(roomId);
      setLastReconcile(result);
      notify(
        t('module.socialTrading.reconcileDone', {
          active: result.markedActive,
          left: result.markedLeft,
          unchanged: result.unchanged,
          im: result.imMemberCount,
        }),
        { type: 'success' }
      );
      await load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setReconciling(false);
    }
  };

  return (
    <PermissionGuard resource="st-room-members" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.roomMembers')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.rooms'), href: paths.socialTrading.rooms.root },
            { name: t('module.socialTrading.roomMembers') },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={load} disabled={loading}>
                {t('module.socialTrading.refresh')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={onReconcile}
                disabled={reconciling || !roomId}
              >
                {t('module.socialTrading.reconcileMembers')}
              </Button>
            </Stack>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          {t('module.socialTrading.roomMembersHint')}
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <TextField
                select
                size="small"
                label={t('module.socialTrading.roomName')}
                value={roomId}
                onChange={(e) => {
                  setRoomId(e.target.value);
                  setPage(0);
                  setLastReconcile(null);
                }}
                sx={{ minWidth: 260 }}
              >
                {rooms.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name} ({r.id.slice(0, 8)}…)
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t('module.socialTrading.userId')}
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value);
                  setPage(0);
                }}
                placeholder="UUID"
                sx={{ minWidth: 280 }}
              />
              <TextField
                select
                size="small"
                label={t('module.socialTrading.memberStatus')}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="ACTIVE">{t('module.socialTrading.memberStatusActive')}</MenuItem>
                <MenuItem value="LEFT">{t('module.socialTrading.memberStatusLeft')}</MenuItem>
              </TextField>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (page !== 0) setPage(0);
                  else load();
                }}
              >
                {t('module.socialTrading.search')}
              </Button>
            </Stack>

            {lastReconcile && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('module.socialTrading.reconcileDone', {
                  active: lastReconcile.markedActive,
                  left: lastReconcile.markedLeft,
                  unchanged: lastReconcile.unchanged,
                  im: lastReconcile.imMemberCount,
                })}
              </Typography>
            )}

            <StScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.socialTrading.userId')}</TableCell>
                  <TableCell>{t('module.socialTrading.memberStatus')}</TableCell>
                  <TableCell>{t('module.socialTrading.messageCount')}</TableCell>
                  <TableCell>{t('module.socialTrading.joinedAt')}</TableCell>
                  <TableCell>{t('module.socialTrading.leftAt')}</TableCell>
                  <TableCell>{t('module.socialTrading.notifications')}</TableCell>
                  <TableCell>{t('module.socialTrading.memberMute')}</TableCell>
                  <TableCell>{t('module.socialTrading.colUpdated')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell><StIdCell value={row.userId} /></TableCell>
                    <TableCell>
                      <StStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>{row.messageCount ?? 0}</TableCell>
                    <TableCell>{stText(stFormatDateTime(row.joinedAt))}</TableCell>
                    <TableCell>{stText(stFormatDateTime(row.leftAt))}</TableCell>
                    <TableCell>
                      <StBooleanChip
                        value={row.notificationsEnabled}
                        trueLabel={t('module.socialTrading.notificationsOn')}
                        falseLabel={t('module.socialTrading.notificationsOff')}
                      />
                    </TableCell>
                    <TableCell>
                      <StBooleanChip
                        value={!!row.mutedAt}
                        trueLabel={t('module.socialTrading.muteOn')}
                        falseLabel={t('module.socialTrading.muteNone')}
                      />
                    </TableCell>
                    <TableCell>{stText(stFormatDateTime(row.updatedAt))}</TableCell>
                  </TableRow>
                ))}
                {!items.length && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        {loading
                          ? t('module.socialTrading.loading')
                          : t('module.socialTrading.roomMembersEmpty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </StScrollTable>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          </CardContent>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
